import { parsePhoneNumberFromString, CountryCode } from "libphonenumber-js";

export type SupportedLanguage = "de" | "en" | "fr" | "nl" | "it" | "es";

export const phoneValidationMessages: Record<SupportedLanguage, {
  required: string;
  invalid: string;
  tooShort: string;
  placeholder: string;
}> = {
  de: {
    required: "Bitte geben Sie Ihre Telefonnummer an.",
    invalid: "Bitte geben Sie eine gültige Telefonnummer im internationalen Format ein (z. B. +43 123 456 789).",
    tooShort: "Die Telefonnummer ist zu kurz. Bitte geben Sie eine vollständige Nummer ein.",
    placeholder: "+43 123 456 789",
  },
  en: {
    required: "Please enter your phone number.",
    invalid: "Please enter a valid phone number in international format (e.g. +43 123 456 789).",
    tooShort: "The phone number is too short. Please enter a complete number.",
    placeholder: "+43 123 456 789",
  },
  fr: {
    required: "Veuillez saisir votre numéro de téléphone.",
    invalid: "Veuillez saisir un numéro de téléphone valide au format international (par ex. +43 123 456 789).",
    tooShort: "Le numéro de téléphone est trop court. Veuillez saisir un numéro complet.",
    placeholder: "+43 123 456 789",
  },
  nl: {
    required: "Vul alstublieft uw telefoonnummer in.",
    invalid: "Vul alstublieft een geldig telefoonnummer in internationaal formaat in (bijv. +43 123 456 789).",
    tooShort: "Het telefoonnummer is te kort. Vul alstublieft een volledig nummer in.",
    placeholder: "+43 123 456 789",
  },
  it: {
    required: "Inserisci il tuo numero di telefono.",
    invalid: "Inserisci un numero di telefono valido in formato internazionale (es. +43 123 456 789).",
    tooShort: "Il numero di telefono è troppo corto. Inserisci un numero completo.",
    placeholder: "+43 123 456 789",
  },
  es: {
    required: "Por favor, introduce tu número de teléfono.",
    invalid: "Por favor, introduce un número de teléfono válido en formato internacional (p. ej. +43 123 456 789).",
    tooShort: "El número de teléfono es demasiado corto. Por favor, introduce un número completo.",
    placeholder: "+43 123 456 789",
  },
};

export interface PhoneValidationResult {
  valid: boolean;
  normalized?: string;
  formatted?: string;
  error?: "required" | "invalid" | "tooShort";
  country?: CountryCode;
}

/**
 * Validates and optionally normalizes a phone number.
 * Accepts international numbers (e.g. +43 123 456 789) and common local formats.
 * Default country is "DE" for parsing numbers without a leading +.
 */
export function validatePhoneNumber(
  value: string,
  defaultCountry: CountryCode = "DE"
): PhoneValidationResult {
  const trimmed = value.trim();

  if (!trimmed) {
    return { valid: false, error: "required" };
  }

  const phoneNumber = parsePhoneNumberFromString(trimmed, defaultCountry);

  if (!phoneNumber) {
    return { valid: false, error: "invalid" };
  }

  if (!phoneNumber.isValid()) {
    // Distinguish obviously too-short inputs from structurally invalid ones
    const digitsOnly = trimmed.replace(/\D/g, "");
    if (digitsOnly.length < 7) {
      return { valid: false, error: "tooShort" };
    }
    return { valid: false, error: "invalid" };
  }

  return {
    valid: true,
    normalized: phoneNumber.format("E.164"),
    formatted: phoneNumber.formatInternational(),
    country: phoneNumber.country,
  };
}

/**
 * Formats a phone number as the user types.
 * Returns the best-effort international formatting or the original input if parsing fails.
 */
export function formatPhoneInput(value: string, defaultCountry: CountryCode = "DE"): string {
  const trimmed = value.trim();
  if (!trimmed) return "";

  const phoneNumber = parsePhoneNumberFromString(trimmed, defaultCountry);
  if (phoneNumber && phoneNumber.isValid()) {
    return phoneNumber.formatInternational();
  }

  // If not yet complete, return cleaned input (digits, +, spaces, -, parentheses)
  return trimmed.replace(/[^+\d\s()-]/g, "");
}
